import { useCallback, useEffect, useMemo, useState } from "react";
import { Users, KeyRound, RefreshCcw, CheckCircle2, Layers } from "lucide-react";
import client from "../lib/api";
import ConfirmDialog from "../components/ConfirmDialog";

/**
 * Seat activation console.
 *
 * Institution and business administrators land here to activate the
 * offline seats their organisation has purchased. Every card and
 * every row on this screen is aggregate: seat state, seat lifecycle
 * timestamps, and a paste field for a person's user id. No holder
 * name, no email, no report, no answer.
 *
 * The three routes this screen speaks:
 *
 *   GET  /licensing/pools                     aggregate pool list
 *   GET  /licensing/pools/:pool_id/seats      seat rows, no PII
 *   POST /licensing/seats/:seat_id/activate   assign + consume
 */
export default function SeatActivationScreen() {
  const [pools, setPools] = useState([]);
  const [selectedPoolId, setSelectedPoolId] = useState(null);
  const [seats, setSeats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [pendingSeatId, setPendingSeatId] = useState(null);
  const [personUserId, setPersonUserId] = useState("");
  const [flash, setFlash] = useState("");
  const [confirmSeatId, setConfirmSeatId] = useState(null);

  const loadPools = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await client.get("/licensing/pools");
      const list = r.data || [];
      setPools(list);
      setSelectedPoolId((cur) => cur || (list.length ? list[0].id : cur));
    } catch (e) {
      setErr(e?.response?.status === 403
        ? "You need an institution administrator role to open this screen."
        : (e?.response?.data?.detail || "Could not load the pool list."));
      setPools([]);
    } finally {
      setBusy(false);
    }
  }, []);

  const loadSeats = useCallback(async (poolId) => {
    if (!poolId) { setSeats([]); return; }
    setBusy(true);
    setErr("");
    try {
      const r = await client.get(`/licensing/pools/${poolId}/seats`);
      setSeats(r.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load seats for this pool.");
      setSeats([]);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { loadPools(); }, [loadPools]);
  useEffect(() => { loadSeats(selectedPoolId); }, [selectedPoolId, loadSeats]);

  const selectedPool = useMemo(
    () => pools.find((p) => p.id === selectedPoolId),
    [pools, selectedPoolId],
  );

  const onActivate = async (seatId) => {
    setPendingSeatId(seatId);
    setErr("");
    setFlash("");
    await client.post(`/licensing/seats/${seatId}/activate`, {
      person_user_id: personUserId.trim(),
    });
    setFlash("Seat activated.");
    setPersonUserId("");
    setConfirmSeatId(null);
    setPendingSeatId(null);
    await Promise.all([loadSeats(selectedPoolId), loadPools()]);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="seat-activation-screen">
      <header className="mb-6 flex items-start justify-between gap-6">
        <div>
          <div className="k-label mb-1 flex items-center gap-2">
            <KeyRound size={14} /> seat activation
          </div>
          <h1 className="klareet-serif text-3xl mb-1" style={{ color: "hsl(var(--klareet-ink))" }}>
            Activate the seats your institution bought
          </h1>
          <p className="k-quiet" style={{ maxWidth: "48rem", lineHeight: 1.55 }}>
            Aggregate counts only. You see how many seats are open, assigned, and consumed; you do not see who consumed which seat. Individual reads belong to the person alone.
          </p>
        </div>
        <button
          type="button"
          onClick={loadPools}
          className="k-btn-outline"
          disabled={busy}
          data-testid="seat-reload-btn"
        >
          <RefreshCcw size={14} /> Refresh
        </button>
      </header>

      {err && (
        <div className="k-card mb-4" style={{ borderColor: "hsl(0 60% 55%)" }} data-testid="seat-error">
          <div className="k-label mb-1" style={{ color: "hsl(0 60% 45%)" }}>error</div>
          <div style={{ color: "hsl(var(--klareet-ink))" }}>{err}</div>
        </div>
      )}

      {flash && (
        <div className="k-card mb-4" data-testid="seat-flash">
          <div className="flex items-center gap-2" style={{ color: "hsl(140 40% 30%)" }}>
            <CheckCircle2 size={16} /> {flash}
          </div>
        </div>
      )}

      {/* Pool list ------------------------------------------------- */}
      <section className="mb-6">
        <div className="k-label mb-2 flex items-center gap-2">
          <Layers size={14} /> pools ({pools.length})
        </div>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="pool-list">
          {pools.map((p) => {
            const used = (p.consumed_seats || 0) + (p.assigned_seats || 0);
            const total = p.total_seats || 0;
            const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
            const active = p.id === selectedPoolId;
            return (
              <button
                type="button"
                key={p.id}
                onClick={() => setSelectedPoolId(p.id)}
                className="rounded-xl p-4 text-left transition-colors"
                style={{
                  background: active
                    ? "hsl(var(--klareet-canvas-2))"
                    : "hsl(var(--klareet-canvas))",
                  border: `1px solid hsl(var(--klareet-line${active ? "" : "-soft"}))`,
                }}
                data-testid={`pool-card-${p.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="klareet-serif text-lg" style={{ color: "hsl(var(--klareet-ink))" }}>
                    {p.label}
                  </div>
                  <div className="k-quiet" style={{ fontSize: "0.85rem" }}>
                    {used} / {total}
                  </div>
                </div>
                <div
                  className="h-2 rounded-full overflow-hidden"
                  style={{ background: "hsl(var(--klareet-line-soft))" }}
                >
                  <div
                    style={{
                      width: `${pct}%`,
                      height: "100%",
                      background: "hsl(var(--klareet-ink))",
                      transition: "width 300ms ease",
                    }}
                    data-testid={`pool-usage-bar-${p.id}`}
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-3 k-quiet" style={{ fontSize: "0.8rem" }}>
                  <span data-testid={`pool-count-consumed-${p.id}`}>
                    consumed {p.consumed_seats || 0}
                  </span>
                  <span data-testid={`pool-count-assigned-${p.id}`}>
                    assigned {p.assigned_seats || 0}
                  </span>
                  <span data-testid={`pool-count-unassigned-${p.id}`}>
                    open {p.unassigned_seats || 0}
                  </span>
                  <span data-testid={`pool-count-revoked-${p.id}`}>
                    revoked {p.revoked_seats || 0}
                  </span>
                </div>
              </button>
            );
          })}
          {!pools.length && !busy && (
            <div className="k-quiet" data-testid="pool-empty">
              No pools yet. Ask your Klareet contact to create one for your cohort.
            </div>
          )}
        </div>
      </section>

      {/* Seat list ------------------------------------------------- */}
      {selectedPool && (
        <section className="mb-4">
          <div className="k-label mb-2 flex items-center gap-2">
            <Users size={14} /> seats in {selectedPool.label} ({seats.length})
          </div>

          <div
            className="rounded-xl overflow-hidden"
            style={{ border: "1px solid hsl(var(--klareet-line-soft))" }}
          >
            <table className="w-full" data-testid="seat-table">
              <thead style={{ background: "hsl(var(--klareet-canvas-2))" }}>
                <tr className="text-left k-label">
                  <th className="px-4 py-2">Seat</th>
                  <th className="px-4 py-2">State</th>
                  <th className="px-4 py-2">Created</th>
                  <th className="px-4 py-2">Consumed</th>
                  <th className="px-4 py-2 text-right">Activate</th>
                </tr>
              </thead>
              <tbody>
                {seats.map((s) => (
                  <tr
                    key={s.id}
                    style={{ borderTop: "1px solid hsl(var(--klareet-line-soft))" }}
                    data-testid={`seat-row-${s.id}`}
                  >
                    <td className="px-4 py-3 klareet-mono" style={{ fontSize: "0.85rem" }}>
                      {s.id.slice(0, 8)}
                    </td>
                    <td className="px-4 py-3" data-testid={`seat-state-${s.id}`}>
                      <SeatStateBadge state={s.state} />
                    </td>
                    <td className="px-4 py-3 k-quiet" style={{ fontSize: "0.85rem" }}>
                      {formatShortDate(s.created_at)}
                    </td>
                    <td className="px-4 py-3 k-quiet" style={{ fontSize: "0.85rem" }}>
                      {formatShortDate(s.consumed_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {s.state === "unassigned" ? (
                        <div className="flex justify-end items-center gap-2">
                          <input
                            type="text"
                            value={pendingSeatId === s.id ? personUserId : ""}
                            onChange={(e) => {
                              setPendingSeatId(s.id);
                              setPersonUserId(e.target.value);
                            }}
                            placeholder="person user id"
                            className="k-input"
                            style={{ width: "16rem", fontSize: "0.85rem" }}
                            data-testid={`seat-userid-input-${s.id}`}
                          />
                          <button
                            type="button"
                            onClick={() => setConfirmSeatId(s.id)}
                            disabled={
                              !personUserId ||
                              pendingSeatId !== s.id ||
                              busy
                            }
                            className="k-btn-primary"
                            data-testid={`seat-activate-btn-${s.id}`}
                          >
                            Activate
                          </button>
                        </div>
                      ) : (
                        <span className="k-quiet" style={{ fontSize: "0.85rem" }}>
                          {s.state === "consumed" ? "already in use" :
                            s.state === "assigned" ? "waiting for first read" :
                              s.state}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {!seats.length && !busy && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 k-quiet text-center">
                      No seats in this pool yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="k-quiet mt-3" style={{ fontSize: "0.8rem" }}>
            You never see whose read is on a seat. That is deliberate: individual reads belong to the person alone.
          </div>
        </section>
      )}

      <ConfirmDialog
        open={!!confirmSeatId}
        onCancel={() => setConfirmSeatId(null)}
        onConfirm={() => onActivate(confirmSeatId)}
        title="Activate this seat?"
        description={<>This assigns and consumes one seat for user <span className="klareet-mono" style={{ fontWeight: 600 }}>{personUserId.trim().slice(0, 12)}</span>. A consumed seat cannot be returned to the open pool.</>}
        consequences={[
          "One seat leaves the open pool for good",
          "The person can start their read on this seat right away",
          "This is recorded in your licensing audit trail",
        ]}
        confirmWord="ACTIVATE"
        confirmLabel="Activate seat"
        tone="warning"
        testId="seat-activate-confirm"
      />
    </div>
  );
}

function SeatStateBadge({ state }) {
  const map = {
    unassigned:  { label: "open",      bg: "hsl(210 40% 92%)", fg: "hsl(210 40% 30%)" },
    assigned:    { label: "assigned",  bg: "hsl(45 60% 92%)",  fg: "hsl(45 60% 30%)"  },
    consumed:    { label: "in use",    bg: "hsl(140 40% 92%)", fg: "hsl(140 40% 30%)" },
    transferred: { label: "moved",     bg: "hsl(280 30% 92%)", fg: "hsl(280 30% 30%)" },
    revoked:     { label: "revoked",   bg: "hsl(0 60% 92%)",   fg: "hsl(0 60% 40%)"   },
  };
  const meta = map[state] || { label: state, bg: "hsl(var(--klareet-canvas-2))", fg: "hsl(var(--klareet-ink))" };
  return (
    <span
      className="klareet-mono"
      style={{
        background: meta.bg,
        color: meta.fg,
        fontSize: "0.75rem",
        padding: "0.15rem 0.55rem",
        borderRadius: "999px",
      }}
    >
      {meta.label}
    </span>
  );
}

function formatShortDate(iso) {
  if (!iso) return "not yet";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}
